export type AiInsightTheme = 'overview' | 'performers' | 'friction' | 'recommendations';

export interface AiInsightRow {
  label?: string;
  value?: string;
  note?: string;
  text?: string;
  url?: string;
  index?: number;
}

export interface AiInsightSection {
  theme: AiInsightTheme;
  title: string;
  icon: string;
  rows: AiInsightRow[];
}

export interface AiSectionConfig {
  theme: AiInsightTheme;
  title: string;
  icon: string;
  /** Lower-case heading phrases the AI may use for this section; a heading must start with one of them. */
  phrases: string[];
}

export interface AiSummaryParseOptions {
  /**
   * Split "Label: 32% - what it means" into a bold value and a regular-weight note,
   * and move sentence-length values into the note. Only enable when the template renders `row.note`.
   */
  splitNotes?: boolean;
  /** Keep "**bold**" markers in row text, for templates that render them themselves. */
  keepBold?: boolean;
}

const EMOJI_PATTERN = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu;

/**
 * Parses an AI report summary into themed sections for card rendering.
 * The first config is used for any lines that appear before the first recognised heading.
 */
export function parseAiInsightSections(
  content: string,
  configs: AiSectionConfig[],
  options: AiSummaryParseOptions = {}
): AiInsightSection[] {
  if (!content || !configs.length) return [];

  // A heading is a short line (optionally "#", "**" or "1." prefixed) that starts with a known section phrase.
  // Body lines such as "VIP Customers: 3" or "1. Reduce drop-offs on checkout" must not open a new section.
  const getSectionConfig = (line: string): AiSectionConfig | null => {
    const clean = line
      .replace(/[#*_]/g, '')
      .replace(/^\s*(section\s*)?\d+[.):]?\s*/i, '')
      .replace(/[:：]\s*$/, '')
      .trim()
      .toLowerCase();
    if (!clean || clean.length > 60 || /[:：]/.test(clean)) return null;

    return (
      configs.find(({ phrases }) => phrases.some((p) => clean.startsWith(p) && clean.length <= p.length + 15)) || null
    );
  };

  const sections: AiInsightSection[] = [];
  let currentSection: AiInsightSection | null = null;
  let recIndex = 1;

  for (let rawLine of content.split('\n')) {
    rawLine = rawLine.replace(EMOJI_PATTERN, '').trim();
    if (!rawLine || /^[#\-*=~_•|\s]+$/.test(rawLine)) continue;

    const headerConfig = getSectionConfig(rawLine);
    if (headerConfig) {
      if (currentSection && currentSection.title === headerConfig.title) continue;
      if (currentSection && currentSection.rows.length > 0) {
        sections.push(currentSection);
      }
      currentSection = { theme: headerConfig.theme, title: headerConfig.title, icon: headerConfig.icon, rows: [] };
      recIndex = 1;
      continue;
    }

    if (!currentSection) {
      const { theme, title, icon } = configs[0];
      currentSection = { theme, title, icon, rows: [] };
    }

    const withoutBullet = rawLine.replace(/^[✓✔•\-*►▶]\s*/u, '');
    const isNumbered = /^\d+[.)]\s+/.test(withoutBullet);
    const cleanLine = withoutBullet
      .replace(/^\d+[.)]\s+/, '')
      .replace(options.keepBold ? /__/g : /\*\*|__/g, '')
      .trim();
    if (!cleanLine) continue;

    const isRecommendation = currentSection.theme === 'recommendations';
    // Only originally numbered lines get a number badge; sub-bullets under a recommendation stay as bullets
    const index = isRecommendation && isNumbered ? recIndex++ : undefined;

    const urlMatch = cleanLine.match(/(https?:\/\/[^\s]+)/);
    const url = urlMatch ? urlMatch[1] : undefined;

    // Require a space after an ASCII colon so times ("10:30") and URLs are not split into label/value.
    // Labels may be in any script (summaries are written in the dashboard language), and Chinese
    // text often uses the full-width colon "：" with no space after it.
    // (Explicit ranges rather than \p{L}: the build targets es2015.)
    const kvMatch = cleanLine.match(
      /^([A-Za-zÀ-ɏ぀-ヿ㐀-鿿豈-﫿가-힯][^:：\n]{0,58})(?::\s+|：\s*)(.+)$/
    );

    if (kvMatch && !kvMatch[2].startsWith('http')) {
      const label = kvMatch[1].trim();
      const rest = kvMatch[2].trim();

      if (!options.splitNotes) {
        currentSection.rows.push({ label, value: rest, index });
      } else if (isRecommendation) {
        // "Action: why and how" - only the action is emphasised
        currentSection.rows.push({ label, note: rest, index });
      } else {
        // "Label: 32% - what it means" - bold only the figure; sentence-length values aren't bolded
        const split = rest.match(/^(.+?)\s+[-–—]\s+(.+)$/);
        if (split) {
          currentSection.rows.push({ label, value: split[1], note: split[2], index });
        } else if (rest.length > 30) {
          currentSection.rows.push({ label, note: rest, index });
        } else {
          currentSection.rows.push({ label, value: rest, index });
        }
      }
    } else if (url) {
      const [before, after] = cleanLine.split(url);
      const labelPart = before.replace(/[:：]\s*$/, '').trim();
      // "Label: https://... - what it means" - drop the separator so the note reads cleanly
      const afterPart = after?.replace(/^\s*[-–—:]\s*/, '').trim();
      currentSection.rows.push({
        label: labelPart || undefined,
        url,
        text: afterPart || (labelPart ? undefined : cleanLine.replace(url, '').trim()) || undefined,
        index,
      });
    } else {
      currentSection.rows.push({ text: cleanLine, index });
    }
  }

  if (currentSection && currentSection.rows.length > 0) {
    sections.push(currentSection);
  }

  return sections;
}
